import React, { useEffect, useRef, useState } from "react";
import { Button } from "./ui/button";
import { Eraser, Check, RotateCcw } from "lucide-react";

/**
 * Signature pad — canvas dessin tactile/souris.
 * Props:
 *  - value: data URL (string) | null  — affiche la signature en lecture seule si non null et readonly=true
 *  - onSave(dataUrl): callback déclenché au clic sur "Valider"
 *  - readonly: bool — si true, affiche uniquement l'image
 *  - height: number (default 160)
 */
export default function SignaturePad({ value = null, onSave, readonly = false, height = 160 }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const [hasInk, setHasInk] = useState(false);
  const [validatedUrl, setValidatedUrl] = useState(value);

  useEffect(() => {
    setValidatedUrl(value);
  }, [value]);

  useEffect(() => {
    if (readonly) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Ajuste la résolution selon le DPR pour un rendu net
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2;
    ctx.strokeStyle = "#0f172a";
  }, [readonly]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    if (e.touches && e.touches[0]) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = getPos(e);
  };

  const draw = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(x, y);
    ctx.stroke();
    lastRef.current = { x, y };
    if (!hasInk) setHasInk(true);
  };

  const stop = () => {
    drawingRef.current = false;
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
  };

  const validate = () => {
    if (!hasInk) return;
    const dataUrl = canvasRef.current.toDataURL("image/png");
    setValidatedUrl(dataUrl);
    onSave && onSave(dataUrl);
  };

  const resign = () => {
    setValidatedUrl(null);
    setHasInk(false);
    // Le canvas se ré-affiche au prochain render (effet sur readonly=false)
    setTimeout(() => {
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }, 50);
  };

  // Affichage signature validée
  if (validatedUrl) {
    return (
      <div className="space-y-2" data-testid="signature-pad-validated">
        <div className="border border-zinc-200 rounded-sm bg-white p-2 flex items-center justify-center" style={{ minHeight: height }}>
          <img src={validatedUrl} alt="Signature" className="max-h-40 object-contain" data-testid="signature-image" />
        </div>
        {!readonly && (
          <Button type="button" variant="outline" size="sm" onClick={resign} data-testid="signature-resign-btn">
            <RotateCcw size={12} className="mr-1" /> Signer à nouveau
          </Button>
        )}
      </div>
    );
  }

  // Mode dessin
  return (
    <div className="space-y-2" data-testid="signature-pad">
      <div
        className="border border-dashed border-zinc-300 rounded-sm bg-white relative"
        style={{ height }}
      >
        <canvas
          ref={canvasRef}
          className="w-full h-full touch-none cursor-crosshair"
          onMouseDown={start}
          onMouseMove={draw}
          onMouseUp={stop}
          onMouseLeave={stop}
          onTouchStart={start}
          onTouchMove={draw}
          onTouchEnd={stop}
          data-testid="signature-canvas"
        />
        {!hasInk && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-xs text-zinc-400 font-mono">
            Signez ici (doigt ou souris)
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" onClick={clear} disabled={!hasInk} data-testid="signature-clear-btn">
          <Eraser size={12} className="mr-1" /> Effacer
        </Button>
        <Button type="button" size="sm" onClick={validate} disabled={!hasInk} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="signature-validate-btn">
          <Check size={12} className="mr-1" /> Valider la signature
        </Button>
      </div>
    </div>
  );
}
